import { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Progress } from './ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { 
  Link2, 
  Upload, 
  Video,
  CheckCircle, 
  AlertCircle, 
  Loader2,
  Crown,
  Info,
  FileVideo,
  X,
  RefreshCw,
  Sparkles,
  Eye,
  EyeOff,
  Users
} from 'lucide-react';
import { cn } from '../lib/utils';
import { API } from '../config/api';
import { safeJson } from '../utils/safeJson';
import { 
  validateFile, 
  uploadFileWithRetry, 
  VIDEO_TYPES, 
  formatFileSize,
  createAcceptString,
  isIOSSafari
} from '../utils/uploadUtils';
import MonetizeToggle from './MonetizeToggle';



// Video DSP Platform configs
const VIDEO_DSP_PLATFORMS = {
  youtube: { name: 'YouTube', icon: '▶️', color: 'bg-red-500', domain: 'youtube.com' },
  vimeo: { name: 'Vimeo', icon: '🎬', color: 'bg-blue-500', domain: 'vimeo.com' },
  vevo: { name: 'Vevo', icon: '🎤', color: 'bg-purple-500', domain: 'vevo.com' }
};

function VideoMethodSelector({ 
  uploadMethod, 
  onMethodChange, 
  tierConfig,
  uploadLimits 
}) {
  const canDirectUpload = tierConfig?.direct_upload_enabled || false;
  
  return (
    <div className="mb-6" data-testid="video-method-selector">
      <Label className="text-sm mb-3 block">Upload Method</Label>
      
      <div className="grid grid-cols-2 gap-3">
        {/* DSP Link Option - Always available */}
        <button
          type="button"
          onClick={() => onMethodChange('dsp')}
          className={cn(
            "p-4 rounded-lg border-2 text-left transition-all",
            uploadMethod === 'dsp' 
              ? "border-red-500 bg-red-500/10" 
              : "border-border hover:border-border"
          )}
          data-testid="video-method-dsp"
        >
          <div className="flex items-center gap-2 mb-2">
            <Link2 className="w-5 h-5 text-red-500" />
            <span className="font-medium">Link from DSP</span>
            <Badge className="ml-auto bg-green-500/20 text-green-400 text-xs">FREE</Badge>
          </div>
          <p className="text-xs text-muted-foreground">
            Link to YouTube, Vimeo, or Vevo
          </p>
        </button>

        {/* Direct Upload Option - Tier-gated */}
        <button
          type="button"
          onClick={() => canDirectUpload && onMethodChange('direct')}
          disabled={!canDirectUpload}
          className={cn(
            "p-4 rounded-lg border-2 text-left transition-all",
            uploadMethod === 'direct' 
              ? "border-purple-500 bg-purple-500/10" 
              : canDirectUpload 
                ? "border-border hover:border-border"
                : "border-border opacity-50 cursor-not-allowed"
          )}
          data-testid="video-method-direct"
        >
          <div className="flex items-center gap-2 mb-2">
            <Upload className="w-5 h-5 text-purple-500" />
            <span className="font-medium">Upload File</span>
            <Badge className="ml-auto bg-purple-500/20 text-purple-400 text-xs">PRO+</Badge>
          </div>
          <p className="text-xs text-muted-foreground">
            {canDirectUpload 
              ? `Upload MP4/MOV/WebM • ${uploadLimits?.direct_uploads_remaining || 0} remaining`
              : "Upgrade to Pro tier for direct uploads"}
          </p>
        </button>
      </div>

      {!canDirectUpload && uploadMethod === 'direct' && (
        <div className="mt-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
          <div className="flex items-start gap-2">
            <Crown className="w-4 h-4 text-amber-500 mt-0.5" />
            <div className="text-xs">
              <p className="font-medium text-amber-400">Upgrade Required</p>
              <p className="text-muted-foreground">
                Direct video uploads are available for Pro ($20/mo) and Elite ($35/mo) artists.
                YouTube/Vimeo links are always free.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function VideoDSPLinkInput({ 
  dspUrl, 
  onUrlChange,
  platform,
  onPlatformDetected
}) {
  const [validating, setValidating] = useState(false);
  const [isValid, setIsValid] = useState(null);

  const validateUrl = async (url) => {
    if (!url) {
      setIsValid(null);
      onPlatformDetected(null);
      return;
    }

    setValidating(true);
    
    // Detect platform from URL
    let detected = null;
    const urlLower = url.toLowerCase();
    
    if (urlLower.includes('youtube.com') || urlLower.includes('youtu.be')) detected = 'youtube';
    else if (urlLower.includes('vimeo.com')) detected = 'vimeo';
    else if (urlLower.includes('vevo.com')) detected = 'vevo';
    
    if (detected) {
      setIsValid(true);
      onPlatformDetected(detected);
    } else {
      setIsValid(false);
      onPlatformDetected(null);
    }
    
    setValidating(false);
  };

  useEffect(() => {
    const debounce = setTimeout(() => {
      validateUrl(dspUrl);
    }, 500);
    return () => clearTimeout(debounce);
  }, [dspUrl]);

  return (
    <div className="space-y-3" data-testid="video-dsp-link-input">
      <div>
        <Label className="text-sm mb-1 block flex items-center gap-2">
          <Link2 className="w-4 h-4 text-red-500" />
          Video Link *
        </Label>
        <div className="relative">
          <Input
            value={dspUrl}
            onChange={(e) => onUrlChange(e.target.value)}
            placeholder="https://www.youtube.com/watch?v=... or Vimeo link"
            className={cn(
              "font-mono text-sm pr-10",
              isValid === true && "border-green-500/50",
              isValid === false && "border-red-500/50"
            )}
            data-testid="video-dsp-url-input"
          />
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            {validating && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
            {!validating && isValid === true && <CheckCircle className="w-4 h-4 text-green-500" />}
            {!validating && isValid === false && <AlertCircle className="w-4 h-4 text-red-500" />}
          </div>
        </div>
      </div>

      {/* Platform detection badge */}
      {platform && (
        <div className="flex items-center gap-2">
          <span className="text-lg">{VIDEO_DSP_PLATFORMS[platform]?.icon}</span>
          <Badge className={cn("text-foreground text-xs", VIDEO_DSP_PLATFORMS[platform]?.color)}>
            {VIDEO_DSP_PLATFORMS[platform]?.name} Detected
          </Badge>
        </div>
      )}

      {/* Accepted platforms */}
      <div className="flex flex-wrap gap-2">
        <span className="text-xs text-muted-foreground">Accepted:</span>
        {Object.entries(VIDEO_DSP_PLATFORMS).map(([key, config]) => (
          <span key={key} className="text-xs text-muted-foreground flex items-center gap-1">
            {config.icon} {config.name}
          </span>
        ))}
      </div>

      {isValid === false && (
        <p className="text-xs text-red-400">
          Please enter a valid YouTube, Vimeo, or Vevo link
        </p>
      )}
    </div>
  );
}

function DirectVideoUploadInput({
  fileUrl,
  onFileUploaded,
  videoFormat,
  tierConfig,
  getAuthHeaders
}) {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const fileInputRef = useRef(null);
  
  const maxSizeMB = tierConfig?.max_file_size_mb || VIDEO_TYPES.maxSizeMB;
  const videoTypes = { ...VIDEO_TYPES, maxSizeMB };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    console.log('[VideoUpload] File selected:', file?.name);
    
    if (!file) return;

    setUploadError(null);
    setShowSuccess(false);

    // Validate file using utility
    const validation = validateFile(file, videoTypes);
    if (!validation.valid) {
      setUploadError(validation.error);
      toast.error(validation.error);
      return;
    }

    console.log('[VideoUpload] File validated:', file.name, formatFileSize(file.size));
    setSelectedFile(file);
    setRetryCount(0);
    toast.success(`Selected: ${file.name}`);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file first');
      return;
    }

    console.log('[VideoUpload] Starting upload for:', selectedFile.name);
    setUploading(true);
    setUploadProgress(0);
    setUploadError(null);
    setShowSuccess(false);

    // Get auth headers
    const headers = { ...getAuthHeaders() };
    delete headers['Content-Type'];

    // Use the resilient upload utility with retry
    const result = await uploadFileWithRetry({
      file: selectedFile,
      url: `${API}/upload/video`,
      headers,
      onProgress: setUploadProgress,
      maxRetries: 3,
      retryDelay: 1000
    });

    setUploading(false);

    if (result.success) {
      console.log('[VideoUpload] Upload success:', result.data);
      setShowSuccess(true);
      onFileUploaded(result.data.file_url, result.data.video_format);
      toast.success('Video file uploaded successfully!');
    } else {
      console.error('[VideoUpload] Upload failed:', result.error);
      setUploadError(result.error);
      setRetryCount(prev => prev + 1);
      toast.error(result.error);
      setUploadProgress(0);
    }
  };

  const handleRemoveFile = () => {
    console.log('[VideoUpload] Removing file');
    setSelectedFile(null);
    setUploadProgress(0);
    setUploadError(null);
    setShowSuccess(false);
    setRetryCount(0);
    onFileUploaded(null, null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-4" data-testid="direct-video-upload-input">
      {!fileUrl ? (
        <>
          {/* File input - properly structured for iOS Safari */}
          <input
            ref={fileInputRef}
            type="file"
            accept={createAcceptString(videoTypes)}
            onChange={handleFileSelect}
            className="hidden"
            id="video-file-input"
            data-testid="video-file-input"
          />
          
          <div 
            className={cn(
              "border-2 border-dashed rounded-lg p-6 text-center transition-all",
              showSuccess
                ? "border-green-500/50 bg-green-500/5"
                : uploadError
                  ? "border-red-500/50 bg-red-500/5"
                  : selectedFile 
                    ? "border-purple-500/50 bg-purple-500/5" 
                    : "border-border hover:border-border"
            )}
          >
            {selectedFile ? (
              <div className="space-y-3">
                {showSuccess ? (
                  <div className="relative">
                    <CheckCircle className="w-10 h-10 mx-auto text-green-500 animate-bounce" />
                    <Sparkles className="w-4 h-4 absolute top-0 right-1/3 text-green-400 animate-pulse" />
                  </div>
                ) : (
                  <FileVideo className={cn(
                    "w-10 h-10 mx-auto",
                    uploadError ? "text-red-500" : "text-purple-500"
                  )} />
                )}
                <div>
                  <p className="font-medium">{selectedFile.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {formatFileSize(selectedFile.size)}
                  </p>
                </div>
                
                {uploading ? (
                  <div className="space-y-2">
                    <Progress value={uploadProgress} className="h-2" />
                    <p className="text-xs text-muted-foreground">
                      {uploadProgress < 95 
                        ? `Uploading... ${uploadProgress}%` 
                        : uploadProgress < 100 
                          ? 'Processing...' 
                          : 'Complete!'}
                    </p>
                  </div>
                ) : showSuccess ? (
                  <p className="text-sm text-green-500 font-medium">Upload complete! ✓</p>
                ) : (
                  <div className="flex items-center justify-center gap-2">
                    <Button 
                      onClick={handleUpload} 
                      size="sm" 
                      type="button"
                      disabled={uploading}
                      data-testid="upload-video-btn"
                    >
                      {retryCount > 0 ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Retry Upload
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          Upload
                        </>
                      )}
                    </Button>
                    <Button 
                      onClick={handleRemoveFile} 
                      variant="outline" 
                      size="sm" 
                      type="button"
                      disabled={uploading}
                      data-testid="remove-video-btn"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <label htmlFor="video-file-input" className="cursor-pointer block">
                <Upload className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
                <p className="font-medium">Tap to select video file</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {videoTypes.displayName} • Max {maxSizeMB}MB
                </p>
                {isIOSSafari() && (
                  <p className="text-xs text-amber-500 mt-2">
                    iOS: Tap the area above to browse files
                  </p>
                )}
              </label>
            )}
          </div>

          {/* Error display with retry hint */}
          {uploadError && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
              <p className="text-sm text-red-400 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{uploadError}</span>
              </p>
              {retryCount > 0 && retryCount < 3 && (
                <p className="text-xs text-muted-foreground mt-1 ml-6">
                  Click "Retry Upload" to try again ({3 - retryCount} attempts remaining)
                </p>
              )}
            </div>
          )}

          {/* Info box */}
          <div className="flex items-start gap-2 p-3 bg-muted/30 rounded-lg">
            <Info className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
            <p className="text-xs text-muted-foreground">
              Direct video uploads are stored on our servers. 
              You must own or have rights to distribute this video.
              {isIOSSafari() && ' Works with iPhone Files app.'}
            </p>
          </div>
        </>
      ) : (
        /* File uploaded state with success animation */
        <div className="border border-green-500/30 bg-green-500/5 rounded-lg p-4" data-testid="video-upload-success-state">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center relative">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <Sparkles className="w-3 h-3 absolute -top-1 -right-1 text-green-400" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-green-400">Upload Complete!</p>
              <p className="text-xs text-muted-foreground">
                {videoFormat?.toUpperCase()} file ready • Now fill in video details below
              </p>
            </div>
            <Button onClick={handleRemoveFile} variant="ghost" size="sm" title="Remove and select different file">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function VideoLicenseAgreement({ accepted, onAcceptChange }) {
  return (
    <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4" data-testid="video-license-agreement">
      <div className="flex items-start gap-3">
        <Checkbox 
          id="video-license-accept"
          checked={accepted}
          onCheckedChange={onAcceptChange}
          data-testid="video-license-checkbox"
        />
        <label htmlFor="video-license-accept" className="text-xs text-muted-foreground cursor-pointer">
          <strong className="text-purple-400">Direct Upload License Agreement:</strong> By uploading video 
          files directly, you represent and warrant that you own or control all rights to the content. 
          You grant DMV Soundstage a non-exclusive, worldwide, royalty-free license to host, stream, 
          reproduce, and display the content for platform operation and promotion.
        </label>
      </div>
    </div>
  );
}

export default function VideoUploadForm({
  onVideoCreated,
  onSuccess,
  getAuthHeaders: propGetAuthHeaders,
  tracks = [],
  artistProfile,
  allowNoTrack = false
}) {
  // Use auth context as fallback if getAuthHeaders not passed as prop
  const auth = useAuth();
  const getAuthHeaders = propGetAuthHeaders || auth.getAuthHeaders;
  
  const [uploadMethod, setUploadMethod] = useState('dsp');
  const [uploadLimits, setUploadLimits] = useState(null);
  const [loading, setLoading] = useState(true);

  // DSP fields
  const [dspUrl, setDspUrl] = useState('');
  const [dspPlatform, setDspPlatform] = useState(null);

  // Direct upload fields
  const [fileUrl, setFileUrl] = useState('');
  const [videoFormat, setVideoFormat] = useState('');
  const [licenseAccepted, setLicenseAccepted] = useState(false);

  // Common fields
  const [title, setTitle] = useState('');
  const [linkedTrackId, setLinkedTrackId] = useState('');
  const [description, setDescription] = useState('');
  const [visibility, setVisibility] = useState('public');
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Monetization state
  const [monetizeConfig, setMonetizeConfig] = useState({
    enabled: false, tip_jar: false, pay_to_unlock: false,
    unlock_price: '', subscription_only: false, boost_after: false,
  });
  const [lockConfig, setLockConfig] = useState(null);

  useEffect(() => {
    fetchUploadLimits();
  }, []);

  const fetchUploadLimits = async () => {
    try {
      const response = await fetch(`${API}/upload/my-limits`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        setUploadLimits(await response.json());
      }
    } catch (error) {
      console.error('Error fetching upload limits:', error);
    } finally {
      setLoading(false);
    }
  };

  const tierConfig = uploadLimits ? {
    direct_upload_enabled: uploadLimits.direct_upload_enabled,
    max_file_size_mb: uploadLimits.max_file_size_mb
  } : null;

  const handleSubmit = async () => {
    // Validate common fields
    if (!title) {
      toast.error('Video title is required');
      return;
    }
    if (!linkedTrackId && !allowNoTrack) {
      toast.error('Please select the track this video belongs to');
      return;
    }

    // Validate method-specific fields
    if (uploadMethod === 'dsp') {
      if (!dspUrl) {
        toast.error('Video link is required');
        return;
      }
      if (!dspPlatform) {
        toast.error('Please enter a valid YouTube, Vimeo, or Vevo link');
        return;
      }
    } else {
      if (!fileUrl) {
        toast.error('Please upload a video file first');
        return;
      }
      if (!licenseAccepted) {
        toast.error('You must accept the license agreement for direct uploads');
        return;
      }
    }

    setSubmitting(true);

    try {
      const videoData = {
        title,
        linked_track_id: linkedTrackId || null,
        upload_method: uploadMethod,
        description: description || null,
        visibility: visibility,
        ownership_confirmed: true
      };

      // Upload thumbnail if selected
      if (thumbnailFile) {
        try {
          const thumbForm = new FormData();
          thumbForm.append('file', thumbnailFile);
          const thumbRes = await fetch(`${API}/upload/image`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: thumbForm,
          });
          if (thumbRes.ok) {
            const thumbData = await thumbRes.json();
            videoData.thumbnail_url = thumbData.url || thumbData.file_url;
          }
        } catch {}
      }

      if (uploadMethod === 'dsp') {
        videoData.dsp_url = dspUrl;
        videoData.dsp_platform = dspPlatform;
        // Set specific URL fields for backwards compatibility
        if (dspPlatform === 'youtube') {
          videoData.youtube_url = dspUrl;
        } else if (dspPlatform === 'vimeo') {
          videoData.vimeo_url = dspUrl;
        }
      } else {
        videoData.file_url = fileUrl;
        videoData.video_format = videoFormat;
        videoData.license_accepted = licenseAccepted;
      }

      const response = await fetch(`${API}/videos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          ...videoData,
          ...(monetizeConfig.enabled ? {
            monetization_settings: {
              tip_jar_enabled: monetizeConfig.tip_jar,
              subscription_only: monetizeConfig.subscription_only,
              boost_after_post: monetizeConfig.boost_after,
            },
            ...(lockConfig && lockConfig.lock_type && lockConfig.lock_type !== 'public' ? { content_lock: lockConfig } : {})
          } : {})
        })
      });

      // Read response safely - use text() once to avoid "body stream already read"
      let result;
      try {
        const responseText = await response.text();
        const parsed = responseText ? JSON.parse(responseText) : {};
        result = {
          ok: response.ok,
          data: response.ok ? parsed : null,
          error: response.ok ? null : (parsed.detail || parsed.message || 'Request failed')
        };
      } catch (parseError) {
        result = {
          ok: response.ok,
          data: response.ok ? {} : null,
          error: response.ok ? null : `Server error (${response.status})`
        };
      }

      if (result.ok && result.data) {
        toast.success('Video added successfully!');
        
        // Reset form
        setDspUrl('');
        setDspPlatform(null);
        setFileUrl('');
        setVideoFormat('');
        setLicenseAccepted(false);
        setTitle('');
        setLinkedTrackId('');
        setDescription('');
        
        // Refresh limits
        fetchUploadLimits();
        
        // Call success callback (supports both prop names)
        if (onSuccess) onSuccess(result.data);
        if (onVideoCreated) onVideoCreated(result.data);
      } else {
        toast.error(result.error || 'Failed to add video');
      }
    } catch (error) {
      console.error('Error creating video:', error);
      toast.error('Failed to add video');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (tracks.length === 0 && !allowNoTrack) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Video className="w-10 h-10 mx-auto mb-3 opacity-50" />
        <p className="font-medium">Add a track first</p>
        <p className="text-xs mt-1">Videos must be linked to an existing track</p>
      </div>
    );
  }

  const canSubmit = title && (linkedTrackId || allowNoTrack) && (
    (uploadMethod === 'dsp' && dspUrl && dspPlatform) ||
    (uploadMethod === 'direct' && fileUrl && licenseAccepted)
  );

  return (
    <div className="space-y-6" data-testid="video-upload-form">
      {/* Upload Method Selector */}
      <VideoMethodSelector
        uploadMethod={uploadMethod}
        onMethodChange={setUploadMethod}
        tierConfig={tierConfig}
        uploadLimits={uploadLimits}
      />

      {/* Linked Track Selector */}
      {tracks.length > 0 && (
        <div>
          <Label className="text-sm mb-1 block">Select Track This Video Belongs To {!allowNoTrack && '*'}</Label>
          <Select value={linkedTrackId} onValueChange={setLinkedTrackId}>
            <SelectTrigger data-testid="video-linked-track-select">
              <SelectValue placeholder="Select a track from your catalog" />
            </SelectTrigger>
            <SelectContent>
              {tracks.map((track) => (
                <SelectItem key={track.track_id} value={track.track_id}>
                  {track.title} {track.is_dsp_verified && '✓ DSP'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* DSP Link Input */}
      {uploadMethod === 'dsp' && (
        <VideoDSPLinkInput
          dspUrl={dspUrl}
          onUrlChange={setDspUrl}
          platform={dspPlatform}
          onPlatformDetected={setDspPlatform}
        />
      )}

      {/* Direct Upload Input */}
      {uploadMethod === 'direct' && tierConfig?.direct_upload_enabled && (
        <>
          <DirectVideoUploadInput
            fileUrl={fileUrl}
            onFileUploaded={(url, format) => {
              setFileUrl(url);
              setVideoFormat(format);
            }}
            videoFormat={videoFormat}
            tierConfig={tierConfig}
            getAuthHeaders={getAuthHeaders}
          />
          
          <VideoLicenseAgreement
            accepted={licenseAccepted}
            onAcceptChange={setLicenseAccepted}
          />
        </>
      )}

      {/* Video Details */}
      <div>
        <Label className="text-sm mb-1 block">Video Title *</Label>
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g., Song Name - Official Video"
          data-testid="video-title-input"
        />
      </div>

      <div>
        <Label className="text-sm mb-1 block">Description (Optional)</Label>
        <Input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Brief description of the video"
          data-testid="video-description-input"
        />
      </div>

      {/* Thumbnail Upload */}
      <div>
        <Label className="text-sm mb-1.5 block">Custom Thumbnail</Label>
        <div className="flex items-center gap-3">
          <div className="w-28 h-16 rounded-lg border border-border overflow-hidden bg-muted/30 flex-shrink-0">
            {thumbnailPreview ? (
              <img src={thumbnailPreview} alt="Thumbnail" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="18" x="3" y="3" rx="2" ry="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg>
              </div>
            )}
          </div>
          <div>
            <label className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border bg-muted/20 hover:bg-muted/40 cursor-pointer text-xs font-medium transition-colors">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" x2="12" y1="3" y2="15"/></svg>
              Choose Thumbnail
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) {
                    setThumbnailFile(f);
                    setThumbnailPreview(URL.createObjectURL(f));
                  }
                }}
                data-testid="video-thumbnail-input"
              />
            </label>
            <p className="text-[10px] text-muted-foreground mt-1">JPG/PNG, max 5MB</p>
          </div>
        </div>
      </div>

      {/* Visibility */}
      <div>
        <Label className="text-sm mb-1.5 block">Visibility</Label>
        <div className="grid grid-cols-3 gap-2">
          {[
            { value: 'public', label: 'Public', icon: Eye },
            { value: 'private', label: 'Private', icon: EyeOff },
            { value: 'subscribers', label: 'Subs Only', icon: Users },
          ].map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setVisibility(opt.value)}
              className={`flex flex-col items-center gap-1 p-2 rounded-lg border text-center transition-all text-[11px] ${
                visibility === opt.value
                  ? 'border-secondary bg-secondary/10 text-foreground font-semibold'
                  : 'border-border text-muted-foreground hover:border-muted-foreground/30'
              }`}
              data-testid={`video-visibility-${opt.value}`}
            >
              <opt.icon className="w-4 h-4" />
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Monetize This Content */}
      <MonetizeToggle
        monetizeConfig={monetizeConfig}
        setMonetizeConfig={setMonetizeConfig}
        lockConfig={lockConfig}
        setLockConfig={setLockConfig}
        compact
      />

      {/* Submit Button */}
      <Button 
        onClick={handleSubmit}
        disabled={!canSubmit || submitting}
        className="w-full"
        data-testid="submit-video-btn"
      >
        {submitting ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Adding Video...
          </>
        ) : (
          <>
            <Video className="w-4 h-4 mr-2" />
            Add {uploadMethod === 'dsp' ? 'DSP' : 'Direct'} Video
          </>
        )}
      </Button>
    </div>
  );
}
